/**
 * Resolve a real visit type (server id / name) to the demo catalog kind.
 * Bindings win; the label match is the fallback until the tenant is bound.
 */
import type { ConfigState, ServerBindings, VisitTypeConfig, VisitTypeKind } from "../state";

const ASSESSMENT_KINDS: VisitTypeKind[] = ["SOC", "ROC", "RECERT", "DISCHARGE_ASSESSMENT"];

export function visitTypeKindFor(
  config: ConfigState,
  bindings: ServerBindings,
  visitTypeId: number | undefined,
  visitTypeName: string | undefined,
): VisitTypeKind | undefined {
  if (visitTypeId !== undefined) {
    const bound = (Object.entries(bindings.visitTypeIdsByKind) as [VisitTypeKind, number | undefined][]).find(([, id]) => id === visitTypeId);
    if (bound) return bound[0];
  }
  if (visitTypeName) {
    const lower = visitTypeName.trim().toLowerCase();
    const byLabel = Object.values(config.visitTypes).find((v) => v.label.toLowerCase() === lower || v.kind.toLowerCase() === lower);
    if (byLabel) return byLabel.kind;
  }
  return undefined;
}

export function visitTypeConfigFor(config: ConfigState, kind: VisitTypeKind | undefined): VisitTypeConfig | undefined {
  return kind ? config.visitTypes[kind] : undefined;
}

export function isAssessmentKind(kind: VisitTypeKind | undefined): boolean {
  return !!kind && ASSESSMENT_KINDS.includes(kind);
}

export function pointsFor(config: ConfigState, kind: VisitTypeKind | undefined, fallback = 1.0): number {
  return kind ? config.visitTypes[kind].points : fallback;
}

export function socVisitTypeIds(bindings: ServerBindings): number[] {
  return ASSESSMENT_KINDS.map((k) => bindings.visitTypeIdsByKind[k]).filter((id): id is number => typeof id === "number");
}

/** The visit type a discipline spends most of its week on (rule-generated supervisory visits excluded). */
export function primaryRoutineKind(discipline: string): VisitTypeKind {
  switch (discipline) {
    case "RN":
    case "LPN":
      return "SN_ROUTINE";
    case "PT":
    case "PTA":
      return "PT_ROUTINE";
    case "OT":
    case "COTA":
      return "OT_ROUTINE";
    case "SLP":
      return "SLP_ROUTINE";
    case "MSW":
      return "MSW_VISIT";
    case "HHA":
      return "HHA_VISIT";
    default:
      return "SN_ROUTINE";
  }
}

export function routineVisitTypeFor(config: ConfigState, discipline: string): VisitTypeConfig {
  return config.visitTypes[primaryRoutineKind(discipline)];
}
